import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';
import { getMockDailyGoals } from './mock-daily-goals';

export interface DailyGoal {
  providerId: string;
  providerName: string;
  date: string;
  goal: number;
  scheduledProduction: number;
  completedProduction: number;
}

export interface GetDailyGoalsResponse {
  data: DailyGoal[];
}

interface GetDailyGoalsVariables {
  date: Date | string | null;
  providerIds?: string[];
  practiceId?: string;
}

// TODO: Currently set to 15 minutes. Verify this interval aligns product requirements
const REFETCH_INTERVAL_IN_SECONDS = 900;

// Helper to check if provider matches
const providerMatches = (goal: DailyGoal, providerIds?: string[]): boolean =>
  !providerIds?.length || providerIds.includes(goal.providerId);

/**
 * Hook to fetch and process daily goals data.
 *
 * This hook:
 * 1. Fetches daily goals from the API (TODO: or uses mock data to be removed once backend is ready).
 *    Date is passed through to the API; axios serializes it (same as useAppointments startDate/endDate).
 * 2. Filters by provider IDs if provided
 * 3. Sorts by scheduled progress (best-performing first)
 *
 * Returns processed data ready for display in the Daily Goals sidebar.
 */
export const useGetDailyGoals = ({
  date,
  providerIds,
  practiceId,
}: GetDailyGoalsVariables) => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  const effectivePracticeId = practiceId || currentPracticeId;
  const path = `${effectivePracticeId}/${endpoints.dailyGoals}`;

  const queryFn = useCallback(async () => {
    // TODO: Remove mock data fallback once backend is ready
    if (
      (process.env.NODE_ENV === 'development' ||
        process.env.NODE_ENV === 'local') &&
      date
    ) {
      return { data: getMockDailyGoals(date) };
    }

    if (!date) return { data: [] };

    return await makeAPIRequestReactQuery<GetDailyGoalsResponse>(
      path,
      {
        date,
        providerIds,
      },
      'GET',
      {
        headers: {
          org: currentOrganizationId,
          practice: effectivePracticeId,
        },
      },
    );
  }, [currentOrganizationId, date, effectivePracticeId, path, providerIds]);

  const result = useQuery<GetDailyGoalsResponse>(
    [endpoints.dailyGoals, effectivePracticeId, date, providerIds],
    queryFn,
    {
      enabled: !!currentOrganizationId && !!effectivePracticeId && !!date,
      refetchInterval: REFETCH_INTERVAL_IN_SECONDS * 1000,
    },
  );

  const filteredAndSortedGoals = useMemo(() => {
    if (!result.data?.data) return [];

    let filtered = [...result.data.data];

    if (providerIds && providerIds.length > 0) {
      filtered = filtered.filter((goal) => providerMatches(goal, providerIds));
    }

    filtered.sort((a, b) => {
      const percentA = a.goal > 0 ? (a.scheduledProduction / a.goal) * 100 : 0;
      const percentB = b.goal > 0 ? (b.scheduledProduction / b.goal) * 100 : 0;
      return percentB - percentA;
    });

    return filtered;
  }, [providerIds, result.data]);

  return { filteredAndSortedGoals, result };
};
